/**
 * Supplier Service
 * TASK-006: Implements supplier management per CONTEXT.md Section 2.2
 *
 * Features:
 * - Full CRUD operations with tenant isolation
 * - Performance metrics tracking
 * - Reliability grade calculation
 * - Risk score computation
 */

import { supabase } from './supabase';
import { logErrorWithTrace } from './api';
import type {
    Supplier,
    SupplierSummary,
    SupplierPerformanceMetrics,
    SupplierContact,
    SupplierCreateInput,
    SupplierUpdateInput,
    SupplierStatus,
    ReliabilityGrade,
    DateRange,
    PaginatedResult,
} from '../types';

// ============================================
// TYPES
// ============================================

export interface SupplierPaginationParams {
    page?: number;
    pageSize?: number;
    sortBy?: 'name' | 'code' | 'reliability_grade' | 'risk_score' | 'created_at';
    sortOrder?: 'asc' | 'desc';
}

export interface SupplierFilterParams {
    status?: SupplierStatus | SupplierStatus[];
    reliabilityGrade?: ReliabilityGrade | ReliabilityGrade[];
    search?: string;
    minRiskScore?: number;
    maxRiskScore?: number;
}

export interface DeliveryTrendData {
    date: string;
    onTimeRate: number;
    deliveriesTotal: number;
    deliveriesOnTime: number;
    deliveriesLate: number;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Map database row to Supplier type
 */
function mapDbToSupplier(row: any): Supplier {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        code: row.code,
        name: row.name,
        contactName: row.contact_name,
        contactEmail: row.contact_email,
        contactPhone: row.contact_phone,
        address: row.address,
        reliabilityGrade: row.reliability_grade || 'C',
        riskScore: row.risk_score ?? 50,
        onTimeDeliveryRate: row.on_time_delivery_rate,
        shortShipRate: row.short_ship_rate,
        averageDelayHours: row.average_delay_hours,
        status: row.status || 'active',
        paymentTerms: row.payment_terms,
        leadTimeDays: row.lead_time_days,
        minimumOrderValue: row.minimum_order_value,
        metadata: row.metadata || {},
        notes: row.notes,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        createdBy: row.created_by,
    };
}

/**
 * Map database row to SupplierSummary type
 */
function mapDbToSupplierSummary(row: any): SupplierSummary {
    return {
        ...mapDbToSupplier(row),
        productCount: row.product_count ?? 0,
        totalDeliveries30d: row.total_deliveries_30d ?? 0,
        totalUnitsOrdered30d: row.total_units_ordered_30d ?? 0,
    };
}

/**
 * Map database row to SupplierPerformanceMetrics type
 */
function mapDbToPerformanceMetrics(row: any): SupplierPerformanceMetrics {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        supplierId: row.supplier_id,
        metricDate: row.metric_date,
        deliveriesTotal: row.deliveries_total ?? 0,
        deliveriesOnTime: row.deliveries_on_time ?? 0,
        deliveriesLate: row.deliveries_late ?? 0,
        deliveriesNoShow: row.deliveries_no_show ?? 0,
        deliveriesEarly: row.deliveries_early ?? 0,
        unitsOrdered: row.units_ordered ?? 0,
        unitsReceived: row.units_received ?? 0,
        unitsShortShipped: row.units_short_shipped ?? 0,
        unitsOverShipped: row.units_over_shipped ?? 0,
        unitsDamaged: row.units_damaged ?? 0,
        avgDelayHours: row.avg_delay_hours,
        maxDelayHours: row.max_delay_hours,
        totalOrderValue: row.total_order_value ?? 0,
        totalReceivedValue: row.total_received_value ?? 0,
        onTimeRate: row.on_time_rate,
        shortShipRate: row.short_ship_rate,
        createdAt: row.created_at,
    };
}

// ============================================
// SUPPLIER SERVICE
// ============================================

export const supplierService = {
    // ============================================
    // CRUD OPERATIONS
    // ============================================

    /**
     * Get paginated list of suppliers
     */
    async getSuppliers(
        pagination?: SupplierPaginationParams,
        filters?: SupplierFilterParams
    ): Promise<PaginatedResult<Supplier>> {
        try {
            const page = pagination?.page ?? 1;
            const pageSize = pagination?.pageSize ?? 20;
            const sortBy = pagination?.sortBy ?? 'name';
            const sortOrder = pagination?.sortOrder ?? 'asc';
            const offset = (page - 1) * pageSize;

            let query = supabase
                .from('suppliers')
                .select('*', { count: 'exact' });

            // Apply filters
            if (filters?.status) {
                const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
                query = query.in('status', statuses);
            }

            if (filters?.reliabilityGrade) {
                const grades = Array.isArray(filters.reliabilityGrade)
                    ? filters.reliabilityGrade
                    : [filters.reliabilityGrade];
                query = query.in('reliability_grade', grades);
            }

            if (filters?.search) {
                query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
            }

            if (filters?.minRiskScore !== undefined) {
                query = query.gte('risk_score', filters.minRiskScore);
            }

            if (filters?.maxRiskScore !== undefined) {
                query = query.lte('risk_score', filters.maxRiskScore);
            }

            // Apply sorting and pagination
            query = query
                .order(sortBy, { ascending: sortOrder === 'asc' })
                .range(offset, offset + pageSize - 1);

            const { data, error, count } = await query;

            if (error) throw error;

            return {
                data: (data || []).map(mapDbToSupplier),
                total: count ?? 0,
                page,
                pageSize,
                totalPages: Math.ceil((count ?? 0) / pageSize),
            };
        } catch (error) {
            logErrorWithTrace('supplierService.getSuppliers', error);
            throw error;
        }
    },

    /**
     * Get supplier summaries with aggregated metrics
     */
    async getSupplierSummaries(
        pagination?: SupplierPaginationParams,
        filters?: SupplierFilterParams
    ): Promise<PaginatedResult<SupplierSummary>> {
        try {
            const page = pagination?.page ?? 1;
            const pageSize = pagination?.pageSize ?? 20;
            const offset = (page - 1) * pageSize;

            // Use the supplier_summary view
            let query = supabase
                .from('supplier_summary')
                .select('*', { count: 'exact' });

            // Apply filters
            if (filters?.status) {
                const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
                query = query.in('status', statuses);
            }

            if (filters?.reliabilityGrade) {
                const grades = Array.isArray(filters.reliabilityGrade)
                    ? filters.reliabilityGrade
                    : [filters.reliabilityGrade];
                query = query.in('reliability_grade', grades);
            }

            if (filters?.search) {
                query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
            }

            query = query.range(offset, offset + pageSize - 1);

            const { data, error, count } = await query;

            if (error) throw error;

            return {
                data: (data || []).map(mapDbToSupplierSummary),
                total: count ?? 0,
                page,
                pageSize,
                totalPages: Math.ceil((count ?? 0) / pageSize),
            };
        } catch (error) {
            logErrorWithTrace('supplierService.getSupplierSummaries', error);
            throw error;
        }
    },

    /**
     * Get a single supplier by ID
     */
    async getSupplier(id: string): Promise<Supplier | null> {
        try {
            const { data, error } = await supabase
                .from('suppliers')
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null; // Not found
                throw error;
            }

            return data ? mapDbToSupplier(data) : null;
        } catch (error) {
            logErrorWithTrace('supplierService.getSupplier', error);
            throw error;
        }
    },

    /**
     * Get supplier by code
     */
    async getSupplierByCode(code: string): Promise<Supplier | null> {
        try {
            const { data, error } = await supabase
                .from('suppliers')
                .select('*')
                .eq('code', code)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }

            return data ? mapDbToSupplier(data) : null;
        } catch (error) {
            logErrorWithTrace('supplierService.getSupplierByCode', error);
            throw error;
        }
    },

    /**
     * Create a new supplier
     */
    async createSupplier(input: SupplierCreateInput, tenantId: string): Promise<Supplier> {
        try {
            const { data, error } = await supabase
                .from('suppliers')
                .insert({
                    tenant_id: tenantId,
                    code: input.code,
                    name: input.name,
                    contact_name: input.contactName,
                    contact_email: input.contactEmail,
                    contact_phone: input.contactPhone,
                    address: input.address,
                    payment_terms: input.paymentTerms,
                    lead_time_days: input.leadTimeDays,
                    minimum_order_value: input.minimumOrderValue,
                    notes: input.notes,
                    metadata: input.metadata || {},
                    status: 'active',
                    reliability_grade: 'C', // Default grade for new suppliers
                    risk_score: 50, // Default risk score
                })
                .select()
                .single();

            if (error) throw error;

            return mapDbToSupplier(data);
        } catch (error) {
            logErrorWithTrace('supplierService.createSupplier', error);
            throw error;
        }
    },

    /**
     * Update a supplier
     */
    async updateSupplier(id: string, input: SupplierUpdateInput): Promise<Supplier> {
        try {
            const updateData: Record<string, unknown> = {};

            if (input.name !== undefined) updateData.name = input.name;
            if (input.contactName !== undefined) updateData.contact_name = input.contactName;
            if (input.contactEmail !== undefined) updateData.contact_email = input.contactEmail;
            if (input.contactPhone !== undefined) updateData.contact_phone = input.contactPhone;
            if (input.address !== undefined) updateData.address = input.address;
            if (input.status !== undefined) updateData.status = input.status;
            if (input.paymentTerms !== undefined) updateData.payment_terms = input.paymentTerms;
            if (input.leadTimeDays !== undefined) updateData.lead_time_days = input.leadTimeDays;
            if (input.minimumOrderValue !== undefined) updateData.minimum_order_value = input.minimumOrderValue;
            if (input.notes !== undefined) updateData.notes = input.notes;
            if (input.metadata !== undefined) updateData.metadata = input.metadata;

            const { data, error } = await supabase
                .from('suppliers')
                .update(updateData)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;

            return mapDbToSupplier(data);
        } catch (error) {
            logErrorWithTrace('supplierService.updateSupplier', error);
            throw error;
        }
    },

    /**
     * Delete a supplier (soft delete by setting status to inactive)
     */
    async deleteSupplier(id: string, hardDelete = false): Promise<boolean> {
        try {
            if (hardDelete) {
                const { error } = await supabase
                    .from('suppliers')
                    .delete()
                    .eq('id', id);

                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('suppliers')
                    .update({ status: 'inactive' })
                    .eq('id', id);

                if (error) throw error;
            }

            return true;
        } catch (error) {
            logErrorWithTrace('supplierService.deleteSupplier', error);
            throw error;
        }
    },

    // ============================================
    // PERFORMANCE METRICS
    // ============================================

    /**
     * Get performance metrics for a supplier
     */
    async getSupplierPerformance(
        supplierId: string,
        dateRange: DateRange
    ): Promise<SupplierPerformanceMetrics[]> {
        try {
            const { data, error } = await supabase
                .from('supplier_performance_metrics')
                .select('*')
                .eq('supplier_id', supplierId)
                .gte('metric_date', dateRange.start)
                .lte('metric_date', dateRange.end)
                .order('metric_date', { ascending: true });

            if (error) throw error;

            return (data || []).map(mapDbToPerformanceMetrics);
        } catch (error) {
            logErrorWithTrace('supplierService.getSupplierPerformance', error);
            throw error;
        }
    },

    /**
     * Get aggregated performance metrics for a supplier
     */
    async getSupplierPerformanceSummary(
        supplierId: string,
        dateRange: DateRange
    ): Promise<{
        totalDeliveries: number;
        onTimeDeliveries: number;
        lateDeliveries: number;
        noShowDeliveries: number;
        onTimeRate: number;
        totalUnitsOrdered: number;
        totalUnitsReceived: number;
        shortShipRate: number;
        avgDelayHours: number;
    }> {
        try {
            const { data, error } = await supabase
                .from('supplier_performance_metrics')
                .select('*')
                .eq('supplier_id', supplierId)
                .gte('metric_date', dateRange.start)
                .lte('metric_date', dateRange.end);

            if (error) throw error;

            const metrics = data || [];

            const totalDeliveries = metrics.reduce((sum, m) => sum + (m.deliveries_total || 0), 0);
            const onTimeDeliveries = metrics.reduce((sum, m) => sum + (m.deliveries_on_time || 0), 0);
            const lateDeliveries = metrics.reduce((sum, m) => sum + (m.deliveries_late || 0), 0);
            const noShowDeliveries = metrics.reduce((sum, m) => sum + (m.deliveries_no_show || 0), 0);
            const totalUnitsOrdered = metrics.reduce((sum, m) => sum + (m.units_ordered || 0), 0);
            const totalUnitsReceived = metrics.reduce((sum, m) => sum + (m.units_received || 0), 0);
            const totalDelayHours = metrics.reduce((sum, m) => sum + (m.avg_delay_hours || 0), 0);

            return {
                totalDeliveries,
                onTimeDeliveries,
                lateDeliveries,
                noShowDeliveries,
                onTimeRate: totalDeliveries > 0 ? (onTimeDeliveries / totalDeliveries) * 100 : 0,
                totalUnitsOrdered,
                totalUnitsReceived,
                shortShipRate: totalUnitsOrdered > 0
                    ? ((totalUnitsOrdered - totalUnitsReceived) / totalUnitsOrdered) * 100
                    : 0,
                avgDelayHours: metrics.length > 0 ? totalDelayHours / metrics.length : 0,
            };
        } catch (error) {
            logErrorWithTrace('supplierService.getSupplierPerformanceSummary', error);
            throw error;
        }
    },

    /**
     * Get delivery trend data for charts
     */
    async getDeliveryTrend(
        supplierId: string,
        months: number = 6
    ): Promise<DeliveryTrendData[]> {
        try {
            const startDate = new Date();
            startDate.setMonth(startDate.getMonth() - months);

            const { data, error } = await supabase
                .from('supplier_performance_metrics')
                .select('*')
                .eq('supplier_id', supplierId)
                .gte('metric_date', startDate.toISOString().split('T')[0])
                .order('metric_date', { ascending: true });

            if (error) throw error;

            return (data || []).map(row => ({
                date: row.metric_date,
                onTimeRate: row.on_time_rate ?? 0,
                deliveriesTotal: row.deliveries_total ?? 0,
                deliveriesOnTime: row.deliveries_on_time ?? 0,
                deliveriesLate: row.deliveries_late ?? 0,
            }));
        } catch (error) {
            logErrorWithTrace('supplierService.getDeliveryTrend', error);
            throw error;
        }
    },

    // ============================================
    // RELIABILITY & RISK
    // ============================================

    /**
     * Calculate reliability grade based on OTD rate
     * Per CONTEXT.md: A=98%+, B=95%+, C=90%+, D=80%+, F=<80%
     */
    calculateReliabilityGrade(onTimeDeliveryRate: number): ReliabilityGrade {
        if (onTimeDeliveryRate >= 98) return 'A';
        if (onTimeDeliveryRate >= 95) return 'B';
        if (onTimeDeliveryRate >= 90) return 'C';
        if (onTimeDeliveryRate >= 80) return 'D';
        return 'F';
    },

    /**
     * Calculate risk score (0-100, lower is better)
     * Based on OTD rate, short-ship rate, and delay hours
     */
    calculateRiskScore(
        onTimeDeliveryRate: number,
        shortShipRate: number,
        avgDelayHours: number
    ): number {
        // Formula: Risk = 100 - (OTD * 0.5) + (ShortShip * 2) + (Delay * 0.5)
        const otdComponent = (onTimeDeliveryRate || 50) * 0.5;
        const shortShipComponent = (shortShipRate || 5) * 2;
        const delayComponent = (avgDelayHours || 0) * 0.5;

        const riskScore = 100 - otdComponent + shortShipComponent + delayComponent;
        return Math.max(0, Math.min(100, riskScore));
    },

    /**
     * Get top performing suppliers
     */
    async getTopPerformingSuppliers(limit: number = 10): Promise<Supplier[]> {
        try {
            const { data, error } = await supabase
                .from('suppliers')
                .select('*')
                .eq('status', 'active')
                .in('reliability_grade', ['A', 'B'])
                .order('risk_score', { ascending: true })
                .limit(limit);

            if (error) throw error;

            return (data || []).map(mapDbToSupplier);
        } catch (error) {
            logErrorWithTrace('supplierService.getTopPerformingSuppliers', error);
            throw error;
        }
    },

    /**
     * Get unreliable suppliers (for review)
     */
    async getUnreliableSuppliers(threshold: number = 70): Promise<Supplier[]> {
        try {
            const { data, error } = await supabase
                .from('suppliers')
                .select('*')
                .eq('status', 'active')
                .gte('risk_score', threshold)
                .order('risk_score', { ascending: false });

            if (error) throw error;

            return (data || []).map(mapDbToSupplier);
        } catch (error) {
            logErrorWithTrace('supplierService.getUnreliableSuppliers', error);
            throw error;
        }
    },

    /**
     * Get suppliers by reliability grade
     */
    async getSuppliersByGrade(grade: ReliabilityGrade): Promise<Supplier[]> {
        try {
            const { data, error } = await supabase
                .from('suppliers')
                .select('*')
                .eq('reliability_grade', grade)
                .eq('status', 'active')
                .order('name');

            if (error) throw error;

            return (data || []).map(mapDbToSupplier);
        } catch (error) {
            logErrorWithTrace('supplierService.getSuppliersByGrade', error);
            throw error;
        }
    },

    // ============================================
    // CONTACTS
    // ============================================

    /**
     * Get contacts for a supplier
     */
    async getSupplierContacts(supplierId: string): Promise<SupplierContact[]> {
        try {
            const { data, error } = await supabase
                .from('supplier_contacts')
                .select('*')
                .eq('supplier_id', supplierId)
                .order('is_primary', { ascending: false });

            if (error) throw error;

            return (data || []).map(row => ({
                id: row.id,
                supplierId: row.supplier_id,
                name: row.name,
                role: row.role,
                email: row.email,
                phone: row.phone,
                isPrimary: row.is_primary ?? false,
                notes: row.notes,
                createdAt: row.created_at,
            }));
        } catch (error) {
            logErrorWithTrace('supplierService.getSupplierContacts', error);
            throw error;
        }
    },

    /**
     * Add a contact to a supplier
     */
    async addSupplierContact(
        supplierId: string,
        contact: Omit<SupplierContact, 'id' | 'supplierId' | 'createdAt'>
    ): Promise<SupplierContact> {
        try {
            const { data, error } = await supabase
                .from('supplier_contacts')
                .insert({
                    supplier_id: supplierId,
                    name: contact.name,
                    role: contact.role,
                    email: contact.email,
                    phone: contact.phone,
                    is_primary: contact.isPrimary,
                    notes: contact.notes,
                })
                .select()
                .single();

            if (error) throw error;

            return {
                id: data.id,
                supplierId: data.supplier_id,
                name: data.name,
                role: data.role,
                email: data.email,
                phone: data.phone,
                isPrimary: data.is_primary ?? false,
                notes: data.notes,
                createdAt: data.created_at,
            };
        } catch (error) {
            logErrorWithTrace('supplierService.addSupplierContact', error);
            throw error;
        }
    },

    // ============================================
    // STATISTICS
    // ============================================

    /**
     * Get supplier statistics summary
     */
    async getSupplierStats(): Promise<{
        total: number;
        active: number;
        byGrade: Record<ReliabilityGrade, number>;
        avgRiskScore: number;
        avgOnTimeRate: number;
    }> {
        try {
            const { data: suppliers, error } = await supabase
                .from('suppliers')
                .select('status, reliability_grade, risk_score, on_time_delivery_rate');

            if (error) throw error;

            const stats = {
                total: suppliers?.length ?? 0,
                active: 0,
                byGrade: { A: 0, B: 0, C: 0, D: 0, F: 0 } as Record<ReliabilityGrade, number>,
                avgRiskScore: 0,
                avgOnTimeRate: 0,
            };

            let totalRiskScore = 0;
            let totalOnTimeRate = 0;
            let countWithOnTimeRate = 0;

            (suppliers || []).forEach(s => {
                if (s.status === 'active') stats.active++;
                if (s.reliability_grade) {
                    stats.byGrade[s.reliability_grade as ReliabilityGrade]++;
                }
                if (s.risk_score != null) {
                    totalRiskScore += s.risk_score;
                }
                if (s.on_time_delivery_rate != null) {
                    totalOnTimeRate += s.on_time_delivery_rate;
                    countWithOnTimeRate++;
                }
            });

            stats.avgRiskScore = stats.total > 0 ? totalRiskScore / stats.total : 0;
            stats.avgOnTimeRate = countWithOnTimeRate > 0 ? totalOnTimeRate / countWithOnTimeRate : 0;

            return stats;
        } catch (error) {
            logErrorWithTrace('supplierService.getSupplierStats', error);
            throw error;
        }
    },
};

export default supplierService;
